/* How this product renders an instant a reader is meant to act on.
 *
 * **Here rather than in one feature, because a second surface needed it and the third would have
 * written its own.** It began in `features/scheduling`, where a schedule's `next_run_at` had to
 * agree with the Time cell above it; the catalogue's source card shows a `Last harvest` beside a
 * connection whose harvest schedule renders through this same function, and two formats for one
 * kind of fact on two screens a reader moves between is the two-vocabularies failure recorded at
 * five sites.
 *
 * **`features/lineage` keeps its own and that is deliberate, not an oversight.** Its instants are
 * edge validity bounds, where the *offset* is part of the fact being read — `2026-08-12 13:23
 * UTC` — so it renders ISO-ordered and preserves the zone rather than resolving into the reader's.
 * Collapsing the two would change what a lineage row states. Whether that is the right call is a
 * separate decision from this move.
 */

/** An instant as `08-09-2026 01:30 AM`, in the zone the schedule was agreed in.
 *
 * **The zone argument is the point, not the format.** These fields arrive as UTC instants, so a
 * schedule set to 01:30 Asia/Kolkata has a `next_run_at` of `2026-09-08T20:00:00+00:00` — and
 * rendering that raw put a 20:00 on a card whose own Time cell says 01:30, which is the one number
 * a reader checks to know when the scan lands. Formatting in the schedule's own zone makes the two
 * agree by construction.
 *
 * Day-first because the display is day-first everywhere else in this product's market, and
 * 12-hour with an explicit AM/PM because that is how the reader deciding a maintenance window
 * says it. The *input* stays 24-hour — see `LOCAL_TIME_PATTERN` — because `13:32` cannot be
 * mistyped as a morning where `1:32` can.
 *
 * **`hourCycle: 'h12'` rather than `hour12: true`**, and the difference is two wrong answers a day.
 * `hour12` is permitted to resolve to the `h11` cycle, which numbers midnight `00` and noon `12` —
 * so a schedule at midnight would render `00:00 AM` and one at noon `12:00 PM` from the same rule.
 * Pinning the cycle gives `12:00 AM` and `12:00 PM`, verified against both.
 *
 * Returns `null` for an absent instant **and for one it cannot format**, so the caller falls back
 * to the raw value rather than to a dash: a dash on `next run` means "nothing is coming", and a
 * formatting failure must not be able to say that.
 */
export function formatInstant(
  iso: string | null | undefined,
  timezone?: string,
): string | null {
  if (iso === null || iso === undefined || iso.trim() === '') {
    return null;
  }
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) {
    return null;
  }
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      /* Undefined falls back to the reader's own zone, which is right for the two instants that
         have no agreed zone at all: a harvest trigger fires from the harvest's clock, so there is
         no configured zone to render in and the reader's own is the only honest choice. */
      ...(timezone === undefined ? {} : { timeZone: timezone }),
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h12',
    }).formatToParts(instant);
    const part = (type: Intl.DateTimeFormatPartTypes): string =>
      parts.find((each) => each.type === type)?.value ?? '';
    const day = part('day');
    const month = part('month');
    const year = part('year');
    const hour = part('hour');
    const minute = part('minute');
    if (day === '' || month === '' || year === '' || hour === '' || minute === '') {
      return null;
    }
    return `${day}-${month}-${year} ${hour}:${minute} ${part('dayPeriod').toUpperCase()}`;
  } catch {
    /* `Intl` throws `RangeError` on a zone it does not know. The stored value is IANA by domain
       guard and by CHECK constraint, so this is unreachable through the API — but an ErrorBoundary
       turns one thrown format call into a blank card, and a raw instant beats a blank card. */
    return null;
  }
}
